import { EventTypes } from '@candulabs/craft';
import { Button } from 'canoe';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { TooltipPlacement } from 'canoe/components/Tooltip/Tooltip';
import { FloatingMenuTab, TabErrorSeverity } from 'craft/containers/FloatingMenu';
import { useEditor } from 'craft/hooks';
import { PreviewFlow } from 'craft/resolvers/Flow/toolbox/PreviewFlow';
import { setToolboxTab, useFlow, useFlowGraph, useToolboxState } from 'craft/state';
import { ToolboxTabs } from 'craft/types';

interface Props {
  flowId: string;
  id: string;
  stepId: string;
  onContextClick: (dom: HTMLButtonElement | null) => void;
  tooltipPlacement: TooltipPlacement;
  isContextOpen?: string;
}

export const FlowStepTab = React.memo(
  ({ flowId, id, stepId, onContextClick, tooltipPlacement, isContextOpen }: Props) => {
    const dispatch = useDispatch();
    const { actions } = useEditor();
    const {
      getPreviousStepId,
      activeStepId,
      stepNames,
      getStepIndex,
      onSetStepId,
      flowNode,
    } = useFlow(flowId);
    const { isStepConnected } = useFlowGraph(flowId);
    const { forceOpenToolbox } = useToolboxState();

    const disconnected = useMemo(() => !isStepConnected(stepId), [isStepConnected, stepId]);

    const selectPreviousTooltipAndOpenInteractions = useCallback(
      (e) => {
        e.stopPropagation();

        onSetStepId(getPreviousStepId(stepId));
        dispatch(setToolboxTab(ToolboxTabs.Interactions));
        // when automatically switching to interactions tab, we want to show the toolbox
        forceOpenToolbox();
      },
      [stepId],
    );

    const errors = useMemo(() => {
      const errorsList = [];
      if (disconnected) {
        errorsList.push({
          severity: TabErrorSeverity.Danger,
          content: (
            <>
              This step is not linked to any other steps. Select a CTA and use{' '}
              <Button
                inline
                appearance="text"
                variant="white"
                size="xsmall"
                showUnderlined
                onClick={selectPreviousTooltipAndOpenInteractions}
              >
                interactions
              </Button>{' '}
              to connect steps.
            </>
          ),
        });
      }

      return errorsList;
    }, [disconnected, selectPreviousTooltipAndOpenInteractions]);

    const stepIndex = useMemo(() => getStepIndex(stepId), [stepId]);
    const stepNodeId = useMemo(() => flowNode?.data.nodes[stepIndex], [flowNode, stepIndex]);
    const nodeName = useMemo(() => stepNames[stepIndex] || `Step ${stepIndex + 1}`, [stepIndex]);

    const tabThumbnail = useMemo(() => <PreviewFlow stepNodeId={stepNodeId} />, [stepId]);

    return (
      <FloatingMenuTab
        id={id}
        nodeId={stepNodeId}
        nodeName={nodeName}
        isActive={activeStepId === stepId}
        tooltipPlacement={tooltipPlacement}
        onSelect={() => {
          onSetStepId(stepId);
          actions.setNodeEvent(EventTypes.selected, [stepNodeId]);
        }}
        onContextClick={onContextClick}
        tabThumbnail={tabThumbnail}
        errors={errors}
        isContextOpen={!!isContextOpen}
      />
    );
  },
);
